import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useLocation } from 'react-router-dom';
import { useGlobalModal } from '../contexts/ModalContext';
import { useResourceCelebration } from '../contexts/ResourceCelebrationContext';
import { useMilestones } from '../contexts/MilestoneContext';
import { useAlertQueueState } from '../contexts/AlertQueueContext';
import { useGameStartAccountOffer } from '../contexts/GameStartAccountOfferContext';
import { useGameResultsPresentation } from '../contexts/GameResultsPresentationContext';
import { useAIHelper } from '../contexts/AIHelperContext';
import { useAds } from '../contexts/AdsContext';
import { getPlayerGameStats } from '../api/gameStats';
import { buildGameResult } from '../utils/gameResults';
import { getCachedPlayerGameStats, getPlayerGameStatsSnapshot, subscribePlayerGameStats } from '../utils/gameStatsCache';
import { getUserId, logGameEvent } from '../analytics';
import GameResultsScreen from '../components/game/GameResultsScreen';

export function useGameResults({
  gameConfig, date, sessionState, puzzleConfig, won, justCompleted,
  sponsored, presentationReady = true, onArchive, archiveLinkProps, onCircuit,
}) {
  const { isLoaded, isSignedIn, userId, getToken } = useAuth();
  const location = useLocation();
  const { openModal, closeModal, isOpen, modalName } = useGlobalModal();
  const { flights } = useResourceCelebration();
  const { hasPendingMilestones } = useMilestones();
  const { hasPendingAlerts } = useAlertQueueState();
  const { hasPendingOffer = false } = useGameStartAccountOffer();
  const { celebrationPending, boardPresentationReady = true, requestCoachmark } = useGameResultsPresentation();
  const { isOpen: aiOpen } = useAIHelper();
  const { isAdBreakActive } = useAds();
  const [statsState, setStatsState] = useState({ identity: null, stats: null, loading: true, error: false });
  const ownerRef = useRef(null);
  const autoOpenedRef = useRef(false);
  const statsRequestRef = useRef(0);
  const fetchedIdentityRef = useRef(null);
  const modalRef = useRef({ isOpen, modalName, closeModal });
  modalRef.current = { isOpen, modalName, closeModal };
  const enabled = /^\d{4}-\d{2}-\d{2}$/.test(date || '') && location.pathname.replace(/\/$/, '') === `/${gameConfig.pathName}/${date}` && isLoaded !== false;
  const identity = isSignedIn ? `clerk:${userId}` : `anonymous:${getUserId()}`;
  const cachedStats = getPlayerGameStatsSnapshot(identity, gameConfig.id);
  const busy = !presentationReady || !boardPresentationReady || celebrationPending || flights.length > 0
    || hasPendingAlerts || hasPendingOffer || aiOpen || isAdBreakActive;
  const canOpen = enabled && !busy && !hasPendingMilestones && !isOpen;
  const result = useMemo(() => buildGameResult(gameConfig.id, sessionState, puzzleConfig, won), [gameConfig.id, sessionState, puzzleConfig, won]);

  const fetchStats = useCallback(async () => {
    const request = ++statsRequestRef.current;
    fetchedIdentityRef.current = identity;
    const cached = getPlayerGameStatsSnapshot(identity, gameConfig.id);
    setStatsState({ identity, stats: cached?.stats || null, loading: !cached, error: false });
    try {
      const response = await getCachedPlayerGameStats(identity, gameConfig.id, async () => {
        // An authenticated stats read must not silently fall back to guest scope.
        const token = isSignedIn ? await getToken({ template: 'basic_user_info_jwt_template' }) : null;
        if (isSignedIn && !token) throw new Error('Missing account token');
        return getPlayerGameStats(gameConfig.id, token);
      });
      if (request === statsRequestRef.current) setStatsState({ identity, stats: response.stats, loading: false, error: false });
    } catch (_) {
      if (request === statsRequestRef.current) setStatsState({ identity, stats: null, loading: false, error: true });
    }
  }, [identity, gameConfig.id, isSignedIn, getToken]);

  useEffect(() => subscribePlayerGameStats(gameConfig.id, () => {
    if (enabled && ownerRef.current === 'results' && fetchedIdentityRef.current === identity) fetchStats();
  }), [enabled, identity, gameConfig.id, fetchStats]);
  useEffect(() => {
    if (!enabled) return undefined;
    let timer;
    const scheduleMidnight = () => {
      const midnight = new Date();
      midnight.setHours(24, 0, 0, 0);
      timer = setTimeout(() => {
        if (ownerRef.current === 'results' && fetchedIdentityRef.current === identity) fetchStats();
        scheduleMidnight();
      }, midnight.getTime() - Date.now() + 50);
    };
    const handleFocus = () => {
      if (ownerRef.current === 'results' && !getPlayerGameStatsSnapshot(identity, gameConfig.id)) fetchStats();
      clearTimeout(timer);
      scheduleMidnight();
    };
    scheduleMidnight();
    window.addEventListener('focus', handleFocus);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('focus', handleFocus);
    };
  }, [enabled, identity, gameConfig.id, fetchStats]);

  useEffect(() => {
    return () => {
      statsRequestRef.current += 1;
      fetchedIdentityRef.current = null;
      const modal = modalRef.current;
      if (ownerRef.current && modal.isOpen && modal.modalName === 'game_results') {
        ownerRef.current = null;
        modal.closeModal('route_change');
      }
    };
  }, [location.key, gameConfig.id, date, sessionState?.session_id]);
  useEffect(() => {
    if (enabled && justCompleted && fetchedIdentityRef.current !== identity) fetchStats();
  }, [enabled, justCompleted, identity, fetchStats]);

  const track = useCallback((event, details = {}) => logGameEvent(event, gameConfig.id, date, details), [gameConfig.id, date]);
  const handleClose = useCallback((reason) => {
    ownerRef.current = null;
    track('results_dismissed', { reason });
    if (!['archive_clicked', 'daily_circuit_clicked', 'route_change', 'puzzle_change'].includes(reason) && gameConfig.id !== 'ortografia') requestCoachmark();
  }, [track, gameConfig.id, requestCoachmark]);
  const handleLella = useCallback(() => closeModal('lella_clicked'), [closeModal]);
  const handleCircuitGame = useCallback((event, targetGame, targetDate, status) => {
    track('results_circuit_clicked', { target_game_slug: targetGame, target_date: targetDate, game_status: status });
    onCircuit(event, targetGame, targetDate);
    if (event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) closeModal('daily_circuit_clicked');
  }, [track, onCircuit, closeModal]);
  const handleArchive = useCallback((event) => {
    onArchive(event, 'results');
    // Modified clicks retain the results screen in the current tab.
    if (event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) closeModal('archive_clicked');
  }, [onArchive, closeModal]);
  const content = useMemo(() => (
    <GameResultsScreen
      result={result} gameConfig={gameConfig}
      stats={cachedStats?.stats || (statsState.identity === identity ? statsState.stats : null)}
      statsLoading={!cachedStats && (statsState.identity !== identity || statsState.loading)}
      statsError={!cachedStats && statsState.identity === identity && statsState.error}
      onRetry={fetchStats} onArchive={handleArchive} archiveLinkProps={archiveLinkProps}
      onLella={handleLella}
      onCircuitGame={handleCircuitGame}
      circuitLinkProps={archiveLinkProps}
    />
  ), [result, gameConfig, statsState, cachedStats, identity, fetchStats, handleArchive, archiveLinkProps, handleLella, handleCircuitGame]);
  const openResults = useCallback((trigger = 'manual') => {
    ownerRef.current = 'results';
    if (trigger === 'manual' || fetchedIdentityRef.current !== identity || !getPlayerGameStatsSnapshot(identity, gameConfig.id)) fetchStats();
    openModal({ title: result.heading, content, alignToGame: true, trapFocus: true, onClose: handleClose,
      analytics: { source_surface: 'game_results', modal_name: 'game_results', game_slug: gameConfig.id, trigger } });
    if (trigger !== 'refresh') track('results_viewed', { trigger, won });
  }, [identity, fetchStats, result.heading, content, openModal, handleClose, gameConfig.id, track, won]);

  useEffect(() => {
    if (justCompleted && !sponsored && canOpen && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      openResults('completion');
    }
  }, [justCompleted, sponsored, canOpen, openResults]);
  useEffect(() => {
    if (ownerRef.current === 'results' && isOpen && modalName === 'game_results') {
      openModal({ title: result.heading, content, alignToGame: true, trapFocus: true, onClose: handleClose,
        analytics: { source_surface: 'game_results', modal_name: 'game_results', game_slug: gameConfig.id, trigger: 'refresh' } });
    }
  }, [isOpen, modalName, openModal, result.heading, content, handleClose, gameConfig.id]);

  useEffect(() => {
    if (enabled && fetchedIdentityRef.current && fetchedIdentityRef.current !== identity) fetchStats();
  }, [identity, enabled, fetchStats]);

  return { canOpen, showEndActions: !busy, openResults: () => { if (canOpen) openResults('manual'); } };
}
